import { useTranslation } from 'react-i18next'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { Text } from '../components/typography'
import CloseIcon from '~/assets/icons/close.svg?react'
import { useAppStore } from '~/hooks'
import { COSMOS_SITES, type CosmosSite, type Dataset } from '~/constants'

import { track } from '~/analytics'

export default function IntroModal({
  isOpen,
  setIsOpen,
  dataset,
}: {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  dataset: Dataset
}) {
  const { t } = useTranslation()
  const enterFromIntro = useAppStore((s) => s.enterFromIntro)
  const hasRegionInURL = useAppStore((s) => s.hasRegionInURL)

  const enter = (destination: CosmosSite | 'waves' | 'erosion') => {
    track('welcome_site_selected', { destination })
    if (destination === 'waves' || destination === 'erosion') {
      track('dataset_selected', { dataset: destination })
    } else {
      track('dataset_selected', { dataset: 'cosmos' })
      track('study_region_selected', { region: destination })
    }
    enterFromIntro(destination)
  }

  const dismiss = (reason: 'explore' | 'close' | 'dismiss') => {
    setIsOpen(false)
    track('welcome_dismissed', { reason })
  }

  const title =
    dataset === 'waves'
      ? t('intro.welcomeCalWaves')
      : dataset === 'erosion'
        ? t('intro.welcomeErosion')
        : t('intro.welcomeCosmos')

  const description =
    dataset === 'waves'
      ? t('intro.calWavesDescription')
      : dataset === 'erosion'
        ? t('intro.erosionDescription')
        : t('intro.cosmosDescription')

  return (
    <Dialog open={isOpen} onClose={() => dismiss('dismiss')} className="relative z-50">
      <div className="fixed inset-0 flex items-start justify-center bg-black bg-opacity-50 p-4 md:items-center">
        <DialogPanel className="relative h-full max-h-full w-full overflow-y-auto rounded-lg bg-[#F8F8F8] md:h-auto md:max-h-[80vh] md:w-[778px] md:rounded-xl md:border">
          <div className="p-5 pb-6 md:p-12 md:pb-6">
            <div className="space-y-4">
              <DialogTitle className="flex items-center justify-between">
                <Text
                  level={1}
                  className="text-[22px] font-bold leading-[33px] text-neutral-midfi-grey"
                >
                  {title}
                </Text>
                <div className="sticky right-0 top-0 z-10 flex justify-end">
                  <button onClick={() => dismiss('close')} className="p-2">
                    <CloseIcon width={32} height={32} />
                  </button>
                </div>
              </DialogTitle>
              <Text level={1} className="text-neutral-midfi-grey">
                {description}
              </Text>

              {hasRegionInURL ? (
                <div className="flex justify-center pb-2 pt-1">
                  <button
                    className="flex h-[52px] w-[180px] items-center justify-center rounded-xl bg-neutral-midfi-grey px-6 py-5 transition-colors hover:bg-neutral-dark-grey sm:h-[42px]"
                    onClick={() => dismiss('explore')}
                  >
                    <Text level={2} className="text-white">
                      {t('common.explore')}
                    </Text>
                  </button>
                </div>
              ) : (
                <div className="space-y-4 pt-1">
                  <div>
                    <Text level={1} className="font-bold text-neutral-midfi-grey">
                      {t('intro.exploreHeader')}
                    </Text>
                    <Text level={2} className="text-neutral-midfi-grey">
                      {t('intro.chooseDestination')}
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:gap-1">
                    {COSMOS_SITES.map((site) => (
                      <button
                        key={site}
                        className="flex h-[36px] items-center justify-center rounded-xl border border-neutral-midfi-grey/30 bg-white px-5 transition-colors hover:bg-neutral-midfi-grey/10 md:h-[52px] md:flex-1"
                        onClick={() => enter(site)}
                      >
                        <Text level={2} className="text-neutral-midfi-grey">
                          {t(`panel.sites.${site}`)}
                        </Text>
                      </button>
                    ))}
                  </div>
                  <hr className="border-neutral-midfi-grey/20" />
                  <div className="flex flex-col gap-2">
                    <button
                      className="group flex items-center gap-1 text-left transition-colors"
                      onClick={() => enter('erosion')}
                    >
                      <Text level={2} className="text-blue-500 group-hover:text-blue-400">
                        {t('intro.erosionLink')}
                      </Text>
                      <span className="text-blue-500 group-hover:text-blue-400" aria-hidden="true">
                        &rarr;
                      </span>
                    </button>
                    <button
                      className="group flex items-center gap-1 text-left transition-colors"
                      onClick={() => enter('waves')}
                    >
                      <Text level={2} className="text-blue-500 group-hover:text-blue-400">
                        {t('intro.calWavesLink')}
                      </Text>
                      <span className="text-blue-500 group-hover:text-blue-400" aria-hidden="true">
                        &rarr;
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
